import type { Query } from '@tanstack/react-query';
import { isTRPCClientError } from '@trpc/client';

/**
 * Portal-local mirror of the web `isUnmountedTrpcQueryFetchError`
 * (packages/web/src/utils/trpcUtils.ts), kept in step with it deliberately —
 * the two apps already keep separate copies of their shared utilities
 * (`errorUtils`, the hook factories), so this follows that boundary rather
 * than introducing a cross-app extraction.
 *
 * Navigating away mid-flight aborts the in-flight fetch, which surfaces as a
 * `TRPCClientError` wrapping a "Failed to fetch"/AbortError. That is a normal
 * lifecycle event, not a failure worth reporting, so the QueryCache reporter
 * drops it when the query has no observers left.
 */
const fetchAbortErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

const isAbortLikeObject = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

const hasFetchAbortError = (error: unknown, isCause = false): boolean => {
  if (isCause && isAbortLikeObject(error)) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (hasFetchAbortError(error.cause, true)) {
    return true;
  }

  if (
    error.name === 'AbortError' ||
    fetchAbortErrorMessages.some((message) => error.message.includes(message))
  ) {
    return true;
  }

  return false;
};

export const isUnmountedTrpcQueryFetchError = (
  error: unknown,
  query: Pick<Query, 'getObserversCount'>
): boolean =>
  isTRPCClientError(error) &&
  query.getObserversCount() === 0 &&
  hasFetchAbortError(error);
